import {groupBy, isNil, memoize, uniq} from "lodash";
import {
    Component,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {ParamTypes} from "../../constans/paramTypes";
import {UnitTypes} from "../../constans/unitTypes";
import {getWeightInputConverter} from "../../utils/UnitUtils";
import DashboardItemContext from "../../context/DashboardItemContext";
import useFilterValueGroups from "../../hooks/useFilterValueGroups";
import {useSQLChartFormatters} from "../../hooks/useSQLChartFormatters";
import {useSQLTableFilterType} from "../../hooks/useSQLTableFilterType";
import {getFarmID} from "../../selectors/farmSelector";
import {colors, colorsNonInvasive} from "../../utils/ColorUtils";
import {
    getParameterFormatter,
    getXAxisFormatterType,
    translateGroupName,
    translateGroupNames,
} from "../../utils/DashboardUtils";
import AsyncTable from "../basics/async-table/AsyncTable";
import {TableIDs} from "../../constans/tableIDs";
import Button from "../basics/button/Button";
import InfoBox from "../basics/info-box/InfoBox";
import Placeholder from "../basics/placeholder/Placeholder";
import AnimalReferenceDocumentModalGrid from "../grids/animal-reference-document/AnimalReferenceDocumentModalGrid";
import SkeletonAreaChart from "../skeletons/SkeletonAreaChart";
import SkeletonBarChart from "../skeletons/SkeletonBarChart";
import SkeletonPieChartSolidWithLegend from "../skeletons/SkeletonPieChartSolidWithLegend";
import SkeletonRadarChart from "../skeletons/SkeletonRadarChart";
import SkeletonScatterChart from "../skeletons/SkeletonScatterChart";
import SQLChart from "./SQLChart";
import fetchData from "../../api/fetch-suspense-wrapper/FetchSuspenseWrapper";
import {getDashboardItemData} from "../../api/dashboard/getDashboardItemData";
import animalsDB from "../../database/animalsDB";
import groupsDB from "../../database/groupsDB";

const getMinMax = memoize(
    (data, parameters, maxYDomain, referenceLines) => {
        let min = Infinity;
        let max = -Infinity;
        let addPadding = false;
        for (let row of data) {
            for (let key of parameters) {
                if (row[key] < min) min = row[key];
                if (row[key] > max) max = row[key];
            }
        }
        for (let line of referenceLines || []) {
            const values = isNil(line.ValueTo)
                ? [line.Value]
                : [line.Value, line.ValueTo];
            for (let v of values) {
                if (v < min) min = v;
                if (v > max) {
                    max = v;
                    addPadding = true;
                }
            }
        }
        if (min === Infinity || max === Infinity) return [0, "auto"];
        min = min < 0 ? min - 1 : 0;
        if (maxYDomain) return [min, +maxYDomain];
        return [min, Math.ceil(max * (addPadding ? 1.25 : 1.05))];
    },
    (data, parameters, maxYDomain, referenceLines = null) =>
        JSON.stringify({data, parameters, maxYDomain, referenceLines})
);

function SQLChartWrapperRenderer({
    Parameters,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    highlight,
    canCreateDomain,
    print,
    filterValue,
    filterType,
    resource,
}) {
    const {t} = useTranslation();

    const {Data, ResultID} = resource.read();

    const context = useContext(DashboardItemContext);

    useEffect(() => {
        if (context?.setResultID) context.setResultID(ResultID);
    }, [ResultID]); // eslint-disable-line react-hooks/exhaustive-deps

    const farm = useSelector(getFarmID);

    const cachedDomain = useRef(null);

    const getFormatter = useSQLChartFormatters();

    const [leftAxisParameters, rightAxisParameters] = useMemo(() => {
        const left = [];
        const right = [];
        for (let param of Parameters) {
            if (!param.show) continue;
            if (param.axis === "left") left.push(param.id);
            if (param.axis === "right") right.push(param.id);
        }
        return [left, right];
    }, [Parameters]);

    const label = useMemo(
        () => Options.GroupName || translateGroupName(group, t),
        [group, t, Options.GroupName]
    );

    const formatters = useMemo(() => {
        const tmp = {};
        // if (type === "Table") {
        //     for (let row of Parameters) {
        //         const type = getParameterFormatter(row, keysDictionary);
        //         tmp[row.id] = {
        //             formatter: getFormatter(type, {precision: row.precision}),
        //             type,
        //         };
        //     }
        // } else {
        for (let row of Parameters) {
            if (!row.show) continue;
            const type = getParameterFormatter(row, keysDictionary);
            const formatter = getFormatter(type, {
                precision: row.precision,
            });
            tmp[row.id] = {formatter, type};
            if (row.axis === "left") {
                if (!tmp.y1) {
                    tmp.y1 = {formatter, type, parameters: []};
                }
                tmp.y1.parameters.push(row.id);
            }
            if (row.axis === "right") {
                if (!tmp.y2) {
                    tmp.y2 = {formatter, type, parameters: []};
                }
                tmp.y2.parameters.push(row.id);
            }
        }
        // }
        const xAxisType = getXAxisFormatterType(
            group,
            keysDictionary,
            Parameters[0]?.parameter
        );

        const options = {precision: Parameters[0]?.precision};

        if (xAxisType === ParamTypes.TIME) {
            options.dateFormat =
                Options.TimePeriod === "week"
                    ? `[${t("week")}] w`
                    : Options.TimePeriod === "month"
                      ? "MMMM"
                      : Options.TimePeriod === "year"
                        ? "YYYY"
                        : "L";

            options.useUTC = !!Options.TimePeriod;
        }

        if (xAxisType) {
            tmp.x = {
                formatter: getFormatter(xAxisType, options),
                type: xAxisType,
            };
        }

        return tmp;
    }, [
        Parameters,
        // type,
        keysDictionary,
        group,
        getFormatter,
        Options.TimePeriod,
        t,
    ]);

    const _data = useMemo(() => {
        // trzeba dopisac recznie klucze, bo recharts nie radzi sobie z obiektami, ktore nie maja wszystkich kluczy
        if (!Options.InsertDomain) return Data;
        const possibleKeys = Data.reduce((prev, curr) => {
            const keys = Object.keys(curr);
            for (let key of keys) {
                prev.add(key);
            }
            return prev;
        }, new Set());
        return Data.map((row) => {
            const tmp = {};
            for (let key of possibleKeys) {
                tmp[key] = row[key];
            }
            return tmp;
        });
    }, [Data, Options.InsertDomain]);

    useEffect(() => {
        cachedDomain.current = null;
    }, [Options.YAxisDomainLeft]);

    const domains = useMemo(() => {
        if (!canCreateDomain) return cachedDomain.current;
        const refLines = groupBy(
            ReferenceLines,
            (refLine) => refLine.Axis || "unknown"
        );
        const leftDomain = getMinMax(
            _data,
            leftAxisParameters,
            Options.YAxisDomainLeft ? +Options.YAxisDomainLeft : null,
            refLines.left
        );
        const rightDomain = getMinMax(
            _data,
            rightAxisParameters,
            Options.YAxisDomainRight ? +Options.YAxisDomainRight : null,
            refLines.right
        );
        cachedDomain.current = [leftDomain, rightDomain];
        return [leftDomain, rightDomain];
    }, [
        canCreateDomain,
        _data,
        leftAxisParameters,
        rightAxisParameters,
        Options.YAxisDomainLeft,
        Options.YAxisDomainRight,
        ReferenceLines,
    ]);

    const groups = useFilterValueGroups(filterType, filterValue);

    const chartColors = useMemo(() => {
        const tmp = {};
        const defaultColors = uniq([
            ...Object.values(colorsNonInvasive),
            ...Object.values(colors),
        ]);
        let lastUsed = 0;
        for (let row of Parameters) {
            if (row.duplicateSeries) {
                if (!row.colors) {
                    for (let group of groups) {
                        tmp[`${row.id}#${group}`] =
                            defaultColors[lastUsed % defaultColors.length];
                        lastUsed++;
                    }
                } else {
                    for (let i = 0; i < groups.length; i++) {
                        tmp[`${row.id}#${groups[i]}`] =
                            row.colors[i % row.colors.length];
                    }
                }
            } else {
                if (type === "Pie") {
                    tmp[row.id] = defaultColors;
                } else if (!row.color) {
                    tmp[row.id] =
                        defaultColors[lastUsed % defaultColors.length];
                    lastUsed++;
                } else {
                    tmp[row.id] = row.color;
                }
            }
        }
        return tmp;
    }, [Parameters, groups, type]);

    const translatedGroupNames = useMemo(
        () => translateGroupNames(filterType, groups, farm),
        [filterType, groups, farm]
    );

    // const frozenHeaders = useMemo(() => {
    //     if (!type === "Table") return [];
    //     let tmp = Parameters.filter((item) => item.freeze).map(
    //         (item) => item.id
    //     );
    //     for (let key in Options.Animal || {}) {
    //         if (Options.Animal[key]) tmp.push(key);
    //     }
    //     return tmp;
    // }, [Parameters, type, Options.Animal]);

    const translatedIDs = useMemo(() => {
        const tmp = {};
        for (let row of Parameters) {
            if (row.show) {
                if (row.duplicateSeries) {
                    for (let group of groups) {
                        tmp[`${row.id}#${group}`] = row.parameterName;
                    }
                } else {
                    tmp[row.id] = row.parameterName;
                }
            }
        }
        return tmp;
    }, [Parameters, groups]);

    // const summaryFields = useMemo(() => {
    //     if (type !== "Table") return [];
    //     return Parameters.filter(
    //         (item) => item.summary && item.summary !== "none"
    //     ).map((item) => ({id: item.id, type: item.summary}));
    // }, [Parameters, type]);

    const legendCalculation = useMemo(() => {
        // if (type === "Table") return null;
        const tmp = {};
        for (let row of Parameters) {
            if (row.duplicateSeries) {
                for (let group of groups) {
                    tmp[`${row.id}#${group}`] = row.legendCalculation;
                }
            } else {
                tmp[row.id] = row.legendCalculation;
            }
        }
        return tmp;
    }, [Parameters, groups]);

    const showOnlyLabelsWithValue = useMemo(() => {
        switch (filterType) {
            case "group":
            case "technologyGroup":
                return group === "group_nr";
            case "cycle":
                return group === "cycle";
            default:
                return false;
        }
    }, [group, filterType]);

    return (
        <div className="h-100">
            <SQLChart
                data={_data}
                chartType={type}
                label={label}
                referenceLines={ReferenceLines}
                secondAxisParameters={rightAxisParameters}
                formatters={formatters}
                onClick={onClick}
                highlight={highlight}
                stack={Options.Stack}
                legend={Options.Legend}
                dataTransposition={!!Options.DataTransposition}
                showAxisName={Options.ShowAxisName}
                leftDomain={domains?.[0]}
                rightDomain={domains?.[1]}
                print={print}
                chartColors={chartColors}
                translatedGroupNames={translatedGroupNames}
                translatedIDs={translatedIDs}
                showIndex={Options.ShowIndex}
                legendCalculation={legendCalculation}
                labelList={Options.LabelList}
                showOnlyLabelsWithValue={showOnlyLabelsWithValue}
            />
        </div>
    );
}

function SQLChartWrapperSkeleton({type}) {
    if (type === "Pie") return <SkeletonPieChartSolidWithLegend />;
    if (type === "Bar") return <SkeletonBarChart />;
    if (type === "Table") return <Placeholder type="table" />;
    if (type === "Scatter") return <SkeletonScatterChart />;
    if (type === "Radar") return <SkeletonRadarChart />;
    return <SkeletonAreaChart />;
}

// function SQLChartWrapperSimple({
//     Parameters,
//     Options,
//     ReferenceLines,
//     group,
//     type,
//     keysDictionary,
//     onClick,
//     highlight,
//     query,
//     canCreateDomain,
//     print,
//     filterValue,
//     filterType,
// }) {
//     const resource = useQueryCallerResource(query);

//     return (
//         <div className="h-100 w-100">
//             {Parameters.length > 0 && (
//                 <Suspense
//                     fallback={
//                         <div className="w-100 h-100" data-wait="true">
//                             <SQLChartWrapperSkeleton type={type} />
//                         </div>
//                     }>
//                     <SQLChartWrapperRenderer
//                         Options={Options}
//                         Parameters={Parameters}
//                         ReferenceLines={ReferenceLines}
//                         group={group}
//                         type={type}
//                         keysDictionary={keysDictionary}
//                         onClick={onClick}
//                         highlight={highlight}
//                         canCreateDomain={canCreateDomain}
//                         print={print}
//                         filterValue={filterValue}
//                         filterType={filterType}
//                         resource={resource}
//                     />
//                 </Suspense>
//             )}
//         </div>
//     );
// }

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    onRetryClick = () => {
        console.error("RETRY");
        this.props.setErrorCounter((prev) => prev + 1);
        // this.setState({hasError: false});
    };

    render() {
        if (this.state.hasError) {
            const {t} = this.props;
            return (
                <>
                    <InfoBox boxColor="error" outlined>
                        <i className="fa-solid fa-exclamation-circle me-2" />
                        <span>
                            {t("newSettings.devices.settingsInfo.noData")}
                        </span>
                    </InfoBox>
                    <div className="text-end">
                        <Button onClick={this.onRetryClick}>
                            {t("retry")}
                        </Button>
                    </div>
                </>
            );
        }

        return this.props.children;
    }
}

function SQLChartWrapperResource({
    Parameters,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    filterItem,
    highlight,
    filterValue,
    print,
    filterType,
    limit,
    dashboardID,
    itemID,
    edit,
    Filter,
    globalFilter,
}) {
    const {t, i18n} = useTranslation();

    const [errorCounter, setErrorCounter] = useState(0);

    const fetchParams = useMemo(() => {
        if (Parameters.length === 0 || !dashboardID) return null;
        return JSON.stringify({
            dashboardID,
            itemID,
            filterItem: filterItem ?? [],
            filterValue,
            limit,
            globalFilter,
            locale: i18n.language,
            overrideParameters: edit ? Parameters : undefined,
            overrideGroup: edit ? group : undefined,
            overrideOptions: edit ? Options : undefined,
            overrideFilter: edit ? Filter : undefined,
            overrideType: edit ? type : undefined,
        });
    }, [
        dashboardID,
        itemID,
        filterItem,
        filterValue,
        limit,
        i18n.language,
        edit,
        Parameters,
        group,
        Options,
        Filter,
        type,
        globalFilter,
    ]);

    const resource = useMemo(() => {
        if (!fetchParams) return null;
        return fetchData(getDashboardItemData(JSON.parse(fetchParams)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fetchParams,
        errorCounter, // dodane, aby mozna bylo podjac probe odswiezenia komponentu
    ]);

    if (!resource) {
        return (
            <div className="h-100 w-100">
                <div className="w-100 h-100" data-wait="true">
                    <SQLChartWrapperSkeleton type={type} />
                </div>
            </div>
        );
    }
    return (
        <div className="h-100 w-100">
            <ErrorBoundary
                key={errorCounter}
                setErrorCounter={setErrorCounter}
                t={t}>
                <Suspense
                    fallback={
                        <div className="w-100 h-100" data-wait="true">
                            <SQLChartWrapperSkeleton type={type} />
                        </div>
                    }>
                    <SQLChartWrapperRenderer
                        Options={Options}
                        Parameters={Parameters}
                        ReferenceLines={ReferenceLines}
                        group={group}
                        type={type}
                        keysDictionary={keysDictionary}
                        onClick={onClick}
                        highlight={highlight}
                        print={print}
                        filterValue={filterValue}
                        filterType={filterType}
                        resource={resource}
                    />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
}

function AnimalComponent({value, object}) {
    const farm = useSelector(getFarmID);

    const animal = useMemo(() => {
        if (!value) return null;
        const tmp =
            animalsDB.getAnimalById(value.id, {
                joinEvents: false,
                findDeleted: true,
            }) || groupsDB.getGroupByAnmGrp(value.id, farm);
        return tmp ?? null;
    }, [value, farm]);

    if (!animal) return value?.name || null;

    return (
        <AnimalReferenceDocumentModalGrid object={animal} value={value?.id} />
    );
}

function SQLChartWrapperTableRenderer({
    dashboardID,
    itemID,
    filterItem,
    filterValue,
    group,
    Parameters,
    keysDictionary,
    Options,
    edit,
    print,
    Filter,
    globalFilter,
}) {
    const {t, i18n} = useTranslation();

    const context = useContext(DashboardItemContext);

    const fetchInitialData = useCallback(
        (limit) => {
            if (Parameters.length === 0 || !dashboardID) return null;
            return getDashboardItemData({
                dashboardID,
                itemID,
                filterItem: filterItem ?? [],
                filterValue,
                limit,
                locale: i18n.language,
                globalFilter,
                overrideParameters: edit ? Parameters : undefined,
                overrideGroup: edit ? group : undefined,
                overrideOptions: edit ? Options : undefined,
                overrideFilter: edit ? Filter : undefined,
                overrideType: edit ? "Table" : undefined,
            });
        },
        [
            dashboardID,
            itemID,
            filterItem,
            filterValue,
            i18n.language,
            Parameters,
            group,
            Options,
            Filter,
            edit,
            globalFilter,
        ]
    );

    const getFormatter = useSQLChartFormatters();

    const getFilterType = useSQLTableFilterType();

    const headers = useMemo(() => {
        let options = {};
        const xAxisType = getXAxisFormatterType(
            group,
            keysDictionary,
            Parameters[0]?.parameter
        );
        if (xAxisType === ParamTypes.TIME) {
            options.dateFormat =
                Options.TimePeriod === "week"
                    ? `[${t("week")}] w`
                    : Options.TimePeriod === "month"
                      ? "MMMM"
                      : Options.TimePeriod === "year"
                        ? "YYYY"
                        : "L";
            options.useUTC = !!Options.TimePeriod;
        }
        let tmp = [];
        for (let item of Parameters) {
            if (!item.show) continue;
            const type = getParameterFormatter(item, keysDictionary);
            const formatter = getFormatter(type, {
                precision: item.precision,
            });
            tmp.push({
                name: item.parameterName,
                field: item.id,
                valueFormatter: formatter,
                // waga wyświetlana w kg - wyrażenia grupowania też w kg
                inputConverter:
                    type === ParamTypes.WEIGHT
                        ? getWeightInputConverter(UnitTypes.MEDIUM)
                        : undefined,
                filterType: getFilterType(type),
                aggregate: item.summary !== "none" ? item.summary : undefined,
            });
        }
        return [
            {
                field: "name",
                name: Options.GroupName || translateGroupName(group, t),
                valueFormatter:
                    xAxisType !== ParamTypes.ANIMAL
                        ? getFormatter(xAxisType, options)
                        : undefined,
                component:
                    xAxisType === ParamTypes.ANIMAL
                        ? AnimalComponent
                        : undefined,
                filterType: getFilterType(xAxisType),
                filterColumn:
                    xAxisType === ParamTypes.ANIMAL ? "name.name" : undefined,
            },
            ...tmp,
        ];
    }, [
        group,
        t,
        Parameters,
        keysDictionary,
        getFormatter,
        Options,
        getFilterType,
    ]);

    return (
        <AsyncTable
            headers={headers}
            fetchInitialData={fetchInitialData}
            shouldIndex={Options.ShowIndex}
            setResultID={context?.setResultID}
            // zapisane widoki tylko na dashboardzie - w edytorze kolumny się zmieniają, wydruk ich nie potrzebuje
            tableId={edit || print ? undefined : TableIDs.dashboardItem(itemID)}
        />
    );
}

export default function SQLChartWrapper({
    Parameters,
    Options,
    ReferenceLines,
    group,
    type,
    keysDictionary,
    onClick,
    filterItem,
    highlight,
    filterValue,
    print,
    filterType,
    limit,
    dashboardID,
    itemID,
    edit = false,
    Filter,
    globalFilter,
}) {
    if (Parameters.length === 0)
        return (
            <div className="w-100 h-100" data-wait="true">
                <SQLChartWrapperSkeleton type={type} />
            </div>
        );
    if (type === "Table") {
        return (
            <SQLChartWrapperTableRenderer
                Options={Options}
                Parameters={Parameters}
                ReferenceLines={ReferenceLines}
                dashboardID={dashboardID}
                filterItem={filterItem}
                filterType={filterType}
                filterValue={filterValue}
                group={group}
                highlight={highlight}
                itemID={itemID}
                keysDictionary={keysDictionary}
                onClick={onClick}
                print={print}
                type={type}
                edit={edit}
                Filter={Filter}
                globalFilter={globalFilter}
            />
        );
    }
    return (
        <SQLChartWrapperResource
            Options={Options}
            Parameters={Parameters}
            ReferenceLines={ReferenceLines}
            dashboardID={dashboardID}
            filterItem={filterItem}
            filterType={filterType}
            filterValue={filterValue}
            group={group}
            highlight={highlight}
            itemID={itemID}
            keysDictionary={keysDictionary}
            limit={limit}
            onClick={onClick}
            print={print}
            type={type}
            edit={edit}
            Filter={Filter}
            globalFilter={globalFilter}
        />
    );
}
